import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // 👇 NEW: Imported useNavigate
import { useOnChange, useUrlState } from '../../hooks/useUrlState';
import api from '../../utils/api';
import { leaveTypeLabel, leaveBreakdownLines } from '../../utils/leaveLabels';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSearch, faFilter, faFileAlt, faLaptopHouse, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import '../../styles/App.css';
import EmployeeAvatar from '../../components/EmployeeAvatar';
import FilterScroller from '../../components/FilterScroller';
import InactiveTag from '../../components/InactiveTag';
import { useRecentSearches } from '../../components/RecentSearches';

/**
 * When the employee submitted the request.
 *
 * Leave and WFH documents carry it as createdAt. Short leaves send appliedAt,
 * because their row is an attendance record that existed before the request.
 * Returns null when it is not known (short leaves applied before it was
 * recorded).
 */
const appliedAtOf = (req) => {
    const raw = req.appliedAt !== undefined ? req.appliedAt : req.createdAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return {
        date: d.toLocaleDateString('en-GB'),
        time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    };
};

/**
 * Who decided a request and when. Requests decided before this was recorded
 * have no name: ones closed from the email link say so, older ones show nothing.
 */
const decidedByOf = (req) => {
    if (req.status === 'Pending') return null;
    const at = req.decidedAt ? new Date(req.decidedAt) : null;
    const when = at && !Number.isNaN(at.getTime())
        ? `${at.toLocaleDateString('en-GB')}, ${at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`
        : '';
    if (req.decidedByName) return { name: req.decidedByName, when };
    if (/Email Quick-Action/i.test(req.adminComment || '')) return { name: 'Via email link', when };
    return null;
};

// The manager's remark, minus the placeholder old email decisions stored.
const remarkOf = (req) => {
    const r = (req.adminComment || '').trim();
    return /^Processed via Email Quick-Action/i.test(r) ? '' : r;
};

const EmployeeRequests = () => {
    const navigate = useNavigate(); // 👇 NEW: Initialized navigate

    // --- DATA & PAGINATION STATES ---
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useUrlState('page', 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // --- UI & FILTER STATES ---
    const [activeTab, setActiveTab] = useUrlState('tab', 'Leaves'); // 'Leaves' or 'WFH'
    const [filterStatus, setFilterStatus] = useUrlState('status', 'Pending');
    // The address holds the search once typing pauses; the box starts from it.
    const [debouncedSearch, setDebouncedSearch] = useUrlState('q', '');
    const [searchTerm, setSearchTerm] = useState(debouncedSearch);
    const recentSearch = useRecentSearches('employee-requests', { value: searchTerm, onPick: setSearchTerm });

    // --- SIDEBAR STATES ---
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // 1. Debounce Search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm, setDebouncedSearch]);

    // 2. Reset Page on Filter/Tab Change
    useOnChange(() => setCurrentPage(1), [activeTab, filterStatus, debouncedSearch]);

    // 3. Fetch Data based on Active Tab
    useEffect(() => {
        fetchRequests(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, activeTab, filterStatus, debouncedSearch]);

    const fetchRequests = async (pageToFetch) => {
        setLoading(true);
        try {
            let endpoint = '';
            if (activeTab === 'Leaves') endpoint = '/leaves/all-requests';
            else if (activeTab === 'WFH') endpoint = '/wfh/all-requests';
            else if (activeTab === 'ShortLeave') endpoint = '/attendance/short-leaves/all-requests';

            const params = {
                page: pageToFetch,
                limit: itemsPerPage,
                search: debouncedSearch,
                status: filterStatus === 'All' ? '' : filterStatus
            };

            const res = await api.get(endpoint, { params });
            
            setRequests(res.data.data || []);
            setTotalPages(res.data.pagination?.totalPages || 1);
            setTotalRecords(res.data.pagination?.totalRecords || 0);
            setCurrentPage(res.data.pagination?.currentPage || 1);
        } catch (err) {
            console.error("Error fetching requests");
            Swal.fire('Error', 'Failed to load requests from server.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id, status, empName) => {
        let actionType = 'request';
        if (activeTab === 'Leaves') actionType = 'leave';
        else if (activeTab === 'WFH') actionType = 'WFH';
        else if (activeTab === 'ShortLeave') actionType = 'short leave';

        let endpointPrefix = '';
        if (activeTab === 'Leaves') endpointPrefix = '/leaves';
        else if (activeTab === 'WFH') endpointPrefix = '/wfh';
        else if (activeTab === 'ShortLeave') endpointPrefix = '/attendance/short-leave';

        const result = await Swal.fire({
            title: `Confirm ${status}?`,
            text: `You are about to ${status.toLowerCase()} ${actionType} for ${empName}.`,
            icon: status === 'Approved' ? 'success' : 'warning',
            input: 'textarea',
            inputPlaceholder: status === 'Rejected'
                ? 'Reason for rejection (required). The employee is emailed this.'
                : 'Optional: Add a remark for the email...',
            inputValidator: (value) => (status === 'Rejected' && !String(value || '').trim())
                ? 'Please give a reason for the rejection' : undefined,
            showCancelButton: true,
            confirmButtonColor: status === 'Approved' ? '#215D7B' : '#d33',
            confirmButtonText: `Yes, ${status}`
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Processing...',
                    text: 'Updating status and emailing the employee.',
                    allowOutsideClick: false,
                    didOpen: () => { Swal.showLoading(); }
                });

                await api.put(`${endpointPrefix}/action/${id}`, {
                    status,
                    adminRemark: result.value || ''
                });

                Swal.fire('Updated!', `Request has been ${status}. Employee notified.`, 'success');
                setIsSidebarOpen(false);
                fetchRequests(currentPage);
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Action failed', 'error');
            }
        }
    };

    const handleDelete = async (id, empName) => {
        let actionType = 'request';
        let endpointPrefix = '';
        if (activeTab === 'Leaves') { actionType = 'leave'; endpointPrefix = '/leaves'; }
        else if (activeTab === 'WFH') { actionType = 'WFH'; endpointPrefix = '/wfh'; }
        else if (activeTab === 'ShortLeave') { actionType = 'short leave'; endpointPrefix = '/attendance/short-leave'; }

        const result = await Swal.fire({
            title: `Delete ${actionType}?`,
            text: `Are you sure you want to permanently delete this ${actionType} request for ${empName}? This action cannot be undone.`,
            icon: 'error',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Deleting...',
                    allowOutsideClick: false,
                    didOpen: () => { Swal.showLoading(); }
                });

                await api.delete(`${endpointPrefix}/${id}`);
                
                Swal.fire('Deleted!', 'Request has been deleted.', 'success');
                setIsSidebarOpen(false);
                fetchRequests(currentPage);
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Delete failed', 'error');
            }
        }
    };

    // --- SIDEBAR HANDLERS ---
    const openSidebar = (req) => {
        recentSearch.save();
        setSelectedRequest(req);
        setIsSidebarOpen(true);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
        setTimeout(() => setSelectedRequest(null), 300);
    };

    return (
        <div className="attendance-container fade-in">
            <h1 className="page-title header-no-margin mb-20">Employee Requests</h1>

            {/* --- TAB NAVIGATION --- */}
            <div className="request-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button 
                    onClick={() => setActiveTab('Leaves')} 
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid light-dark(#e2e8f0, var(--theme-border))', background: activeTab === 'Leaves' ? '#215D7B' : 'var(--theme-surface)', color: activeTab === 'Leaves' ? '#fff' : 'var(--theme-muted)', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                    <FontAwesomeIcon icon={faFileAlt} /> Paid Leaves
                </button>
                
                <button 
                    onClick={() => setActiveTab('WFH')} 
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid light-dark(#e2e8f0, var(--theme-border))', background: activeTab === 'WFH' ? '#215D7B' : 'var(--theme-surface)', color: activeTab === 'WFH' ? '#fff' : 'var(--theme-muted)', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                    <FontAwesomeIcon icon={faLaptopHouse} /> Work From Home
                </button>

                <button 
                    onClick={() => setActiveTab('ShortLeave')} 
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid light-dark(#e2e8f0, var(--theme-border))', background: activeTab === 'ShortLeave' ? '#215D7B' : 'var(--theme-surface)', color: activeTab === 'ShortLeave' ? '#fff' : 'var(--theme-muted)', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                    <FontAwesomeIcon icon={faEye} /> Short Leaves
                </button>
            </div>

            {/* --- FILTER & SEARCH BAR --- */}
            <div className="filter-bar-card fade-in">
                <FilterScroller>
                    {['All', 'Pending', 'Approved', 'Rejected'].map(status => (
                        <button
                            key={status}
                            className={`gts-btn filter-btn capitalize ${filterStatus === status ? 'primary active' : 'warning inactive'}`}
                            onClick={() => setFilterStatus(status)}
                        >
                            {status === 'All' && <FontAwesomeIcon icon={faFilter} className="filter-icon" />}
                            {status}
                        </button>
                    ))}
                </FilterScroller>

                <div className="search-wrapper">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search Employee..."
                        className="swal2-input search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>
            </div>

            {/* --- TABLE --- */}
            <div className="employee-table-container fade-in">
                <ResponsiveTable className="employee-table">
                    <thead>
                        <tr>
                            <th>Employee</th>
                            {(activeTab === 'Leaves' || activeTab === 'ShortLeave') && <th>Leave Type</th>}
                            <th>Applied At</th>
                            <th>Dates</th>
                            <th>Duration</th>
                            <th>Status</th>
                            <th>Decided By</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={activeTab === 'Leaves' || activeTab === 'ShortLeave' ? "8" : "7"} className="empty-table-message">Loading Requests...</td></tr>
                        ) : requests.length === 0 ? (
                            <tr>
                                <td colSpan={activeTab === 'Leaves' || activeTab === 'ShortLeave' ? "8" : "7"} className="empty-table-message">
                                    No {filterStatus !== 'All' ? filterStatus.toLowerCase() : ''} {activeTab === 'Leaves' ? 'leave' : activeTab === 'ShortLeave' ? 'short leave' : 'WFH'} requests found.
                                </td>
                            </tr>
                        ) : (
                            requests.map(req => (
                                <tr key={req._id}>
                                    {/* 👇 UPDATED: Clickable Name */}
                                    <td data-label="Employee">
                                        <div className="flex-row gap-10">
                                            <EmployeeAvatar person={req.userId} />
                                            <div>
                                                <div
                                                    className="fw-bold text-primary"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => { if (!req.userId?._id) return; recentSearch.save(); navigate(`/employee/${req.userId._id}`); }}
                                                    title="View Profile"
                                                >
                                                    {req.userId?.name || 'Unknown'}
                                                    <InactiveTag person={req.userId} />
                                                </div>
                                                {req.userId?.employeeId && (
                                                    <div className="text-small text-muted">{req.userId.employeeId}</div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {(activeTab === 'Leaves' || activeTab === 'ShortLeave') && (
                                        <td data-label="Leave Type">
                                            <span className="role-tag employee text-small">{leaveTypeLabel(req)}</span>
                                        </td>
                                    )}

                                    <td data-label="Applied At" className="text-small">
                                        {(() => {
                                            const at = appliedAtOf(req);
                                            return at ? (
                                                <>
                                                    <div className="fw-600 text-dark-gray">{at.date}</div>
                                                    <div className="text-muted">{at.time}</div>
                                                </>
                                            ) : <span className="text-muted" title="Not recorded for older requests">—</span>;
                                        })()}
                                    </td>

                                    <td data-label="Dates" className="text-dark-gray text-small fw-600">
                                        {new Date(req.fromDate).toLocaleDateString('en-GB')}
                                        <span className="text-muted mx-1">➜</span>
                                        {new Date(req.toDate).toLocaleDateString('en-GB')}
                                    </td>

                                    <td data-label="Duration" className="fw-500">
                                        {req.days} Days
                                    </td>

                                    <td data-label="Status">
                                        <span className={`status-badge ${
                                            req.status === 'Approved' ? 'success' :
                                            req.status === 'Rejected' ? 'danger' : 'warning'
                                        }`}>
                                            {req.status}
                                        </span>
                                        {req.status === 'Rejected' && remarkOf(req) && (
                                            <div className="text-small text-muted" style={{ marginTop: '4px', maxWidth: '200px', whiteSpace: 'pre-wrap' }}>
                                                Reason: {remarkOf(req)}
                                            </div>
                                        )}
                                    </td>

                                    <td data-label="Decided By" className="text-small">
                                        {(() => {
                                            const d = decidedByOf(req);
                                            return d ? (
                                                <>
                                                    <div className="fw-600 text-dark-gray">{d.name}</div>
                                                    {d.when && <div className="text-muted">{d.when}</div>}
                                                </>
                                            ) : <span className="text-muted" title="Not recorded for older requests">—</span>;
                                        })()}
                                    </td>

                                    <td data-label="Action">
                                        <div className="flex-row gap-5">
                                            <button
                                                className="gts-btn doc-btn"
                                                style={{ padding: '6px 10px', background: 'light-dark(#f1f5f9, var(--theme-surface-raised))', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }}
                                                onClick={() => openSidebar(req)}
                                                title="View Details"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>

                                            {req.status === 'Pending' && (
                                                <>
                                                    <button
                                                        className="gts-btn primary btn-small"
                                                        onClick={() => handleAction(req._id, 'Approved', req.userId.name)}
                                                        title="Approve"
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </button>
                                                    <button
                                                        className="gts-btn danger btn-small"
                                                        onClick={() => handleAction(req._id, 'Rejected', req.userId.name)}
                                                        title="Reject"
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} />
                                                    </button>
                                                </>
                                            )}

                                            <button
                                                className="gts-btn btn-small"
                                                style={{ background: 'light-dark(#fef2f2, var(--theme-danger-soft))', color: '#ef4444', border: '1px solid light-dark(#fecaca, var(--theme-border-strong, #4a4e55))' }}
                                                onClick={() => handleDelete(req._id, req.userId?.name)}
                                                title="Delete Request"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </ResponsiveTable>
            </div>

            {!loading && (
                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    limit={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    onLimitChange={(newLimit) => {
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                />
            )}

            <div className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`} onClick={closeSidebar}></div>
            <div className={`expense-detail-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                {selectedRequest && (
                    <>
                        <div className="sidebar-header">
                            <div>
                                <h2 className="sidebar-title">{activeTab === 'Leaves' ? 'Leave Request' : activeTab === 'ShortLeave' ? 'Short Leave Request' : 'WFH Request'}</h2>
                                <span className={`status-badge ${selectedRequest.status === 'Approved' ? 'success' : selectedRequest.status === 'Rejected' ? 'danger' : 'warning'}`} style={{ padding: '4px 8px', fontSize: '10px' }}>
                                    {selectedRequest.status}
                                </span>
                            </div>
                            <button className="sidebar-close-btn" onClick={closeSidebar}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        
                        <div className="sidebar-content">
                            <h3 className="sidebar-section-title">Request Details</h3>
                            <div className="detail-grid-2">
                                {/* 👇 UPDATED: Clickable Name inside Sidebar */}
                                <div className="detail-group">
                                    <span className="detail-label">Employee</span>
                                    <span 
                                        className="detail-value fw-bold text-primary"
                                        style={{ cursor: 'pointer'}}
                                        onClick={() => {
                                            if (selectedRequest.userId?._id) {
                                                closeSidebar();
                                                navigate(`/employee/${selectedRequest.userId._id}`);
                                            }
                                        }}
                                        title="View Profile"
                                    >
                                        {selectedRequest.userId?.name || 'N/A'}
                                    </span>
                                </div>
                                <div className="detail-group">
                                    <span className="detail-label">Duration</span>
                                    <span className="detail-value">{selectedRequest.days} Days</span>
                                </div>
                                
                                {(activeTab === 'Leaves' || activeTab === 'ShortLeave') && (
                                    <div className="detail-group" style={{ gridColumn: 'span 2' }}>
                                        <span className="detail-label">Leave Type</span>
                                        <span className="detail-value">{leaveTypeLabel(selectedRequest)}</span>
                                        {leaveBreakdownLines(selectedRequest).map(line => (
                                            <span key={line} className="detail-value text-small text-muted" style={{ display: 'block', fontWeight: 400 }}>{line}</span>
                                        ))}
                                    </div>
                                )}

                                <div className="detail-group" style={{ gridColumn: 'span 2' }}>
                                    <span className="detail-label">Applied At</span>
                                    <span className="detail-value">
                                        {appliedAtOf(selectedRequest)
                                            ? `${appliedAtOf(selectedRequest).date}, ${appliedAtOf(selectedRequest).time}`
                                            : 'Not recorded'}
                                    </span>
                                </div>

                                <div className="detail-group">
                                    <span className="detail-label">From Date</span>
                                    <span className="detail-value">{new Date(selectedRequest.fromDate).toLocaleDateString('en-GB')}</span>
                                </div>
                                <div className="detail-group">
                                    <span className="detail-label">To Date</span>
                                    <span className="detail-value">{new Date(selectedRequest.toDate).toLocaleDateString('en-GB')}</span>
                                </div>

                                <div className="detail-group" style={{ gridColumn: 'span 2' }}>
                                    <span className="detail-label">Reason provided by employee</span>
                                    <span className="detail-value" style={{ background: 'light-dark(#f8fafc, var(--theme-surface-soft))', padding: '10px', borderRadius: '6px', border: '1px solid light-dark(#e2e8f0, var(--theme-border))', whiteSpace: 'pre-wrap' }}>
                                        {selectedRequest.reason || 'No reason provided.'}
                                    </span>
                                </div>

                                {decidedByOf(selectedRequest) && (
                                    <div className="detail-group" style={{ gridColumn: 'span 2' }}>
                                        <span className="detail-label">{selectedRequest.status} by</span>
                                        <span className="detail-value">
                                            {decidedByOf(selectedRequest).name}
                                            {decidedByOf(selectedRequest).when ? `, ${decidedByOf(selectedRequest).when}` : ''}
                                        </span>
                                    </div>
                                )}

                                {remarkOf(selectedRequest) && (
                                    <div className="detail-group" style={{ gridColumn: 'span 2' }}>
                                        <span className="detail-label" style={{ color: 'light-dark(#ea580c, var(--theme-warning-ink, #f0c27a))' }}>
                                            {selectedRequest.status === 'Rejected' ? 'Reason for rejection' : 'Manager remark'}
                                        </span>
                                        <span className="detail-value" style={{ background: 'light-dark(#fef3c7, var(--theme-warning-soft))', padding: '10px', borderRadius: '6px', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                                            "{remarkOf(selectedRequest)}"
                                        </span>
                                    </div>
                                )}
                            </div>

                            {selectedRequest.status === 'Pending' && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
                                    <button 
                                        className="gts-btn primary" 
                                        style={{ flex: 1, justifyContent: 'center' }}
                                        onClick={() => handleAction(selectedRequest._id, 'Approved', selectedRequest.userId?.name)}
                                    >
                                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '5px' }} /> Approve
                                    </button>
                                    <button 
                                        className="gts-btn danger" 
                                        style={{ flex: 1, justifyContent: 'center' }}
                                        onClick={() => handleAction(selectedRequest._id, 'Rejected', selectedRequest.userId?.name)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} /> Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default EmployeeRequests;
